import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { TruncatedTooltipValue } from '@/components/layout/TruncatedTooltipValue';
import { formatDate } from '@/lib/dateFormatter';
import { formatAmount } from '@/lib/format';
import { AffiliateReferralReport } from '@/services/dashboard/referralReports/referralReports.types';
import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';

const textCell = (value: string | number) => (
  <div className="whitespace-nowrap text-xs font-medium text-foreground-detail">{value}</div>
);

export const getAffiliateColumns = (t: (key: string) => string): ColumnDef<AffiliateReferralReport>[] => [
  {
    accessorKey: 'sn',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
    cell: ({ row, table }) => (
      <div className="font-medium truncate max-w-[10rem]">{getSerialNumber(table, row.index)}</div>
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'affiliateInfo',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('common.page.tableColumn.affiliateInformation')} />
    ),
    cell: ({ row }) => {
      const url = row.original.subDomainUrl;
      return (
        <div className="flex flex-col gap-1 whitespace-nowrap">
          <Link
            className="block max-w-[15rem] truncate text-xs font-medium text-info underline"
            to="/dashboard/referral-reports/$reportType/$referrerId"
            search={{ breadcrumbReturnTo: window.location.href, referCode: row.original.referCode }}
            params={{ reportType: 'affiliate', referrerId: row.original.affiliateId }}
            state={{
              affiliateReferralName: row.original.affiliateInfo,
              affiliateReferralIsFromSubDomain: row.original.isFromSubDomain,
              affiliateReferralSubDomainUrl: `https://${url}`,
            }}
          >
            {row.getValue<string>('affiliateInfo') ? row.getValue('affiliateInfo') : '-'}
          </Link>
          <span className="text-xs font-medium text-foreground-subtle">{row.original.emailAddress}</span>
        </div>
      );
    },
  },
  {
    accessorKey: 'referredTo',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.referredTo')} />,
    cell: ({ row }) => (
      <TruncatedTooltipValue
        content={row.getValue<string>('referredTo')}
        className="max-w-[15rem] text-xs font-medium text-foreground-detail"
      />
    ),
  },
  {
    accessorKey: 'userType',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.form.label.userType')} />,
    cell: ({ row }) =>
      textCell(
        row.getValue('userType') === 'Customer' ? t('common.form.label.customer') : t('common.form.label.affiliate'),
      ),
  },
  {
    id: 'createdDate',
    accessorFn: (row) => row.actionDate,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.createdDate')} />,
    cell: ({ row }) => textCell(formatDate(row.getValue<number>('createdDate'), 'YYYY/MM/DD HH:mm:ss')),
    enableColumnFilter: true,
  },
  {
    id: 'rewardPoints',
    accessorKey: 'rewardPoints',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('common.page.tableColumn.rewardBalance')} />
    ),
    cell: ({ row }) => (
      <div className="whitespace-nowrap text-right text-xs font-medium text-foreground-detail">
        {formatAmount(row.getValue<number>('rewardPoints') || 0, ' ')} 円
      </div>
    ),
    meta: { align: 'right' },
  },
  {
    id: 'updatedDate',
    accessorFn: (row) => row.actionDate,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.updatedDate')} />,
    cell: ({ row }) => textCell(formatDate(row.getValue<number>('updatedDate'), 'YYYY/MM/DD HH:mm:ss')),
  },
];
